import React, { useState } from "react";
import { useRouter } from "next/router";
import { CircularProgress, Grid } from "@mui/material";
import { swalError, swalMessage } from "@unisearch/helpers";
import API from "@unisearch/helpers/api";
import { Field, Form, Formik } from "formik";
import Link from "next/link";
import Select from "react-select";
import * as Yup from "yup";
import styles from "./form.module.scss";

const selectStyles = {
  control: (styles) => ({
    ...styles,
    backgroundColor: "white",
    fontSize: "14px",
    padding: "5px",
    border: "1px solid #ebe9f1 !important",
    boxShadow: "0 !important",
    "&:hover": {
      border: "1px solid #ebe9f1 !important",
    },
  }),
  option: (styles, { isDisabled, isFocused, isSelected }) => {
    const color = "#333";
    return {
      ...styles,
      fontSize: "13px",
      color: isSelected ? "#F47F20" : "#B5B5C3",
      backgroundColor: isSelected
        ? "#F5F6FC"
        : isFocused
        ? "#F5F6FC"
        : styles.backgroundColor,
      "&:hover": {
        backgroundColor: "#F5F6FC",
        color: "#F47F20",
      },
      cursor: isDisabled ? "not-allowed" : "pointer",
    };
  },
  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
  menu: (provided) => ({ ...provided, zIndex: 9999 }),
  placeholder: (defaultStyles) => {
    return {
      ...defaultStyles,
      color: "#c2c2c2",
      fontSize: "13px",
    };
  },
};

export const WebnerForm = ({
  title,
  subTitle,
  subject,
  subjectHTML,
  bodyHTML,
}) => {
  const _init = {
    name: "",
    subject,
    email: "",
    phone: "",
    countryName: "",
    intake: "",
    studyLevel: "",
    program: "",
    funding: "",
    location: "",
    subjectHTML,
    bodyHTML,
    terms_and_condition1: 1,
    terms_and_condition: false,
  };

  const [initialState, setInitialState] = useState(_init);
  const [loading, setLoading] = useState(false);
  const isFormInable = (values, errors) => {
    if (
      errors &&
      Object.keys(errors).length === 0 &&
      values.name &&
      values.email
    ) {
      return false;
    } else {
      return true;
    }
  };

  const intakeModuleArr = [
    { value: "JAN", label: "JAN" },
    { value: "MAY", label: "MAY" },
    { value: "SEPT", label: "SEPT" },
  ];

  const countryArr = [
    { value: "Australia", label: "Australia" },
    { value: "USA", label: "USA" },
    { value: "UK", label: "UK" },
    { value: "Canada", label: "Canada" },
    { value: "Ireland", label: "Ireland" },
    { value: "New Zealand", label: "New Zealand" },
  ];
  const educationArr = [
    { value: "Undergraduate", label: "Undergraduate" },
    { value: "Postgraduate", label: "Postgraduate" },
  ];
  const fundArr = [
    { value: "Self Funded", label: "Self Funded" },
    { value: "Parents", label: "Parents" },
    { value: "Education Loan", label: "Education Loan" },
  ];

  // form validation rules
  const phoneRegExp = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    email: Yup.string()
      .email()
      .typeError("Please provide valid email")
      .required("Email is required"),
    phone: Yup.string()
      .matches(phoneRegExp, "Contact number is not valid")
      .min(9)
      .max(15)
      .required("Contact number is required"),

    // countryName: Yup.string().required("Country name is required"),
    intake: Yup.string().required("Intake is required"),
    studyLevel: Yup.string().required("Study Level is required"),
    funding: Yup.string().required("This field is required"),

    terms_and_condition: Yup.bool().oneOf(
      [true],
      "You must accept the terms and conditions"
    ),
  });

  const handleRegister = async ({ ...values }, actions) => {
    delete values.terms_and_condition;
    setLoading(true);

    await API.post("public/leads", values).then((resp) => {
      if (resp.error) {
        swalError("Something went wrong");
        setLoading(false);
      }
      if (!resp.error) {
        swalMessage("Welcome!", "You have successfully registered");
        actions.resetForm();
        setLoading(false);
        // swalSuccess("You have successfully registered", "Welcome!");
      }
    });
  };
  return (
    <section id="form" className={styles.form_section}>
      <div className="container">
        <div className={styles.form_wrapper}>
          <div className={styles.form_title_wrapper}>
            <h2>{title}</h2>
            <p>{subTitle}</p>
          </div>

          <Formik
            initialValues={initialState}
            enableReinitialize={true}
            validationSchema={validationSchema}
            onSubmit={(values, actions) => {
              handleRegister(values, actions);
            }}
          >
            {({ handleSubmit, values, setFieldValue, errors, touched }) => (
              <Form className="form" autoComplete="off">
                <Grid container columnSpacing={{ xs: 2, sm: 2, md: 4 }}>
                  <Grid item xs={12} sm={6} md={6}>
                    <div className={styles.form_group}>
                      <label>
                        Full Name <span className={styles.asterisk}>*</span>
                      </label>
                      <input
                        id="name"
                        name="name"
                        placeholder="Type Full Name"
                        type="text"
                        value={values.name ?? ""}
                        onChange={(e) => {
                          setFieldValue("name", e.target.value);
                          touched.name = true;
                        }}
                      />
                      {errors.name && touched.name ? (
                        <div className={styles.error_msg}>{errors.name}</div>
                      ) : null}
                    </div>
                  </Grid>

                  <Grid item xs={12} sm={6} md={6}>
                    <div className={styles.form_group}>
                      <label>
                        Email Address
                        <span className={styles.asterisk}>*</span>
                      </label>
                      <input
                        id="email"
                        name="email"
                        placeholder="Type Your Email "
                        type="email"
                        value={values.email ?? ""}
                        onChange={(e) => {
                          setFieldValue("email", e.target.value);
                          touched.email = true;
                        }}
                      />
                      {errors.email && touched.email ? (
                        <div className={styles.error_msg}>{errors.email}</div>
                      ) : null}
                    </div>
                  </Grid>

                  <Grid item xs={12} sm={6} md={6}>
                    <div className={styles.form_group}>
                      <label>
                        Phone Number
                        <span className={styles.asterisk}>*</span>
                      </label>
                      <input
                        id="phone"
                        name="phone"
                        placeholder="Type Your Phone Number"
                        type="number"
                        value={values.phone ?? ""}
                        onChange={(e) => {
                          setFieldValue("phone", e.target.value);
                          touched.phone = true;
                        }}
                      />
                      {errors.phone && touched.phone ? (
                        <div className={styles.error_msg}>{errors.phone}</div>
                      ) : null}
                    </div>
                  </Grid>

                  {/* <Grid item xs={12} sm={12} md={6}>
                    <div className={styles.form_group}>
                      <label>
                        Preferred Destination Country{" "}
                        <span className={styles.asterisk}>*</span>
                      </label>
                      <Select
                        instanceId="countryName"
                        styles={selectStyles}
                        placeholder="Please Select"
                        options={countryArr ?? []}
                        name="countryName"
                        onChange={(item) => {
                          setFieldValue("countryName", item.value);
                          touched.countryName = true;
                        }}
                        value={
                          countryArr
                            ? countryArr.filter(
                                (item) => item.value == values?.countryName
                              )
                            : {}
                        }
                      />
                      {errors.countryName && touched.countryName ? (
                        <div className={styles.error_msg}>
                          {errors.countryName}
                        </div>
                      ) : null}
                    </div>
                  </Grid> */}

                  <Grid item xs={12} sm={6} md={6}>
                    <div className={styles.form_group}>
                      <label>
                        Preferred Intake{" "}
                        <span className={styles.asterisk}>*</span>
                      </label>
                      <Select
                        instanceId="intake"
                        styles={selectStyles}
                        placeholder="Please Select"
                        options={intakeModuleArr ?? []}
                        name="intake"
                        onChange={(item) => {
                          setFieldValue("intake", item.value);
                          touched.intake = true;
                        }}
                        value={
                          intakeModuleArr
                            ? intakeModuleArr.filter(
                                (item) => item.value == values?.intake
                              )
                            : {}
                        }
                      />
                      {errors.intake && touched.intake ? (
                        <div className={styles.error_msg}>{errors.intake}</div>
                      ) : null}
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <div className={styles.form_group}>
                      <label>
                        Study Level <span className={styles.asterisk}>*</span>
                      </label>
                      <Select
                        instanceId="studyLevel"
                        styles={selectStyles}
                        placeholder="Please Select"
                        options={educationArr ?? []}
                        name="studyLevel"
                        onChange={(item) => {
                          setFieldValue("studyLevel", item.value);
                          touched.studyLevel = true;
                        }}
                        value={
                          educationArr
                            ? educationArr.filter(
                                (item) => item.value == values?.studyLevel
                              )
                            : {}
                        }
                      />
                      {errors.studyLevel && touched.studyLevel ? (
                        <div className={styles.error_msg}>
                          {errors.studyLevel}
                        </div>
                      ) : null}
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <div className={styles.form_group}>
                      <label>Preferred Subject</label>
                      <input
                        id="program"
                        name="program"
                        placeholder="Type Preferred Subject"
                        type="text"
                        value={values.program ?? ""}
                        onChange={(e) => {
                          setFieldValue("program", e.target.value);
                        }}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <div className={styles.form_group}>
                      <label>
                        Funding <span className={styles.asterisk}>*</span>
                      </label>
                      <Select
                        instanceId="funding"
                        styles={selectStyles}
                        placeholder="Please Select"
                        options={fundArr ?? []}
                        name="funding"
                        onChange={(item) => {
                          setFieldValue("funding", item.value);
                          touched.funding = true;
                        }}
                        value={
                          fundArr
                            ? fundArr.filter(
                                (item) => item.value == values?.funding
                              )
                            : {}
                        }
                      />
                      {errors.funding && touched.funding ? (
                        <div className={styles.error_msg}>{errors.funding}</div>
                      ) : null}
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <div className={styles.form_group}>
                      <label>Current Location</label>
                      <input
                        id="location"
                        name="location"
                        placeholder="Type Your City Here"
                        type="text"
                        value={values.location ?? ""}
                        onChange={(e) => {
                          setFieldValue("location", e.target.value);
                        }}
                      />
                    </div>
                  </Grid>
                </Grid>

                <p className={styles.promise_text}>
                  UniSearch will not share your details with others without your
                  permission:
                </p>

                <div className={styles.checkbox_container_wrapper}>
                  <div className={styles.checkbox_with_error_wrapper}>
                    <div className={styles.checkbox_wrapper}>
                      <label className={styles.checkbox_lebel} html="terms">
                        <Field
                          id="terms"
                          type="checkbox"
                          name="terms_and_condition"
                          className="checkbox disable-team team_values"
                          value={true}
                          checked={
                            values.terms_and_condition &&
                            values.terms_and_condition === true
                              ? true
                              : false
                          }
                          onChange={(event) => {
                            setFieldValue(
                              "terms_and_condition",
                              !values.terms_and_condition
                            );
                            touched.terms_and_condition = true;
                          }}
                        />
                        <span style={{ marginLeft: "10px" }}>
                          I agree to UniSearch
                        </span>
                      </label>
                      <Link legacyBehavior href="/terms-and-conditions">
                        <a className={styles.term_link}>Terms</a>
                      </Link>
                      <span>and</span>
                      <Link legacyBehavior href="/privacy-policy">
                        <a className={styles.term_link}>Privacy Policy.</a>
                      </Link>
                    </div>

                    {errors.terms_and_condition &&
                    touched.terms_and_condition ? (
                      <div className={styles.error_msg}>
                        {errors.terms_and_condition}
                      </div>
                    ) : null}
                  </div>
                </div>

                <button
                  type="submit"
                  className={`${
                    styles.register_btn
                  } btn btn-secondary btn-bg btn-full ${
                    loading || isFormInable(values, errors)
                      ? styles.disabled
                      : "enable"
                  } `}
                  onClick={handleSubmit}
                  disabled={
                    loading || isFormInable(values, errors) ? true : false
                  }
                >
                  {loading && <CircularProgress size="16px" color="inherit" />}
                  Register Now!
                </button>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </section>
  );
};

export default WebnerForm;
